import { useLatest } from 'module/hooks/use-latest';
import { useNavigate, useLocation } from '@rocketlane/rocket-router';
import { fromPairs, toPairs } from 'lodash';
import { parse, ParsedQuery, stringify } from 'query-string';
import { useCallback, useMemo } from 'react';

export interface Transformer<T> {
  from: (parsed: ParsedQuery) => T;
  to: (value: T) => ParsedQuery;
}

export const DEFAULT_TRANSFORMER: Transformer<ParsedQuery> = {
  from: (parsed) => parsed,
  to: (value) => value,
};

const sanitize = (value: ParsedQuery): ParsedQuery =>
  fromPairs(toPairs(value).filter(([, x]) => x != null));

export const useSearch = <T = ParsedQuery>({ from, to }: Transformer<T>) => {
  const { search, hash } = useLocation();
  const navigate = useNavigate();
  const parsed = useMemo(() => sanitize(parse(search)), [search]);
  const value = useMemo(() => from(parsed), [parsed, from]);
  const getNext = useCallback(
    (next: T) => `?${stringify(sanitize({ ...parsed, ...to(next) }))}`,
    [parsed, to],
  );
  const setValue = useCallback(
    (next: T, replace = false) => {
      const next_search = getNext(next);

      if (search === next_search) {
        return;
      }

      navigate(
        {
          hash,
          search: next_search,
        },
        { replace },
      );
    },
    [getNext, hash, navigate, search],
  );
  const setValueLatest = useLatest(setValue);
  const getNextLatest = useLatest(getNext);

  return [value, setValueLatest, getNextLatest] as [
    T,
    (value: T, replace?: boolean) => void,
    (value: T) => string,
  ];
};
